/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  UserAllowAccess,
  UserDataToTransfer,
  UserDetailsContactDto,
  UserDetailsDto,
  UserDetailsIntranetDto,
  UserDetailsSalesPersonDto,
  UserDetailsVerifyDto,
  UserPositionDto,
  UserStatus,
  UserValidateLoginDto,
} from '@gh/builder-shared-data';
import { cleanUserName, getDefaultEmail, getEmailLocalName } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';
import { defaults, fromPairs, omit, pick, pickBy } from 'lodash';
import { UserListItem } from './user-list-item.model';

export type UserDetailsContact = UserDetailsContactDto;
export type UserDetails = WithOptimisticLock<WithPrivilegedToken<{
  status: UserStatus;
  userEmail: string;
  sendEmailsFrom?: string;
  userId?: ID;
  userToReassignLeadsTo?: UserDetailsSalesPersonDto;
  transfer?: string;
  contact?: UserDetailsContactDto;
  role?: number;
  position?: UserPosition;
  systemRoles: Hash<boolean>;
  confirmations?: any;
  allowAccessToLeads?: UserAllowAccess;
  leadsOf?: Hash<boolean>;
  intranetGuid?: ID;
  intranetUser?: string;
  intranetPassword?: string;
  isIntranetVerified: boolean;
  usersFromOtherOfficesAccess: boolean;
  allowAccessToMultipleOffices: boolean;
  publishToWeb: boolean;
  firstInLocation?: boolean;
  userDataToTransfer?: UserDataToTransfer;
  existingUser?:boolean;
}>>;
export type UserValidateLogin = UserValidateLoginDto;
export type UserPosition = UserPositionDto;
export type UserDetailsVerify = UserDetailsVerifyDto;
export type UserDetailsIntranet = UserDetailsIntranetDto;
export type UserIntranetCredentials = { intranetUser: string; intranetPassword: string; hasRequiredError: boolean };

export const getNormalizedEmailName = (contact: UserDetailsContact) => {
  const firstName =  contact.firstName ? contact.firstName.split(' ')[0] : '';
  return cleanUserName(contact.firstName && contact.lastName
                         ? `${firstName}.${contact.lastName}`
                         : contact.name);
};

export function convertUserDetailsToDto(user: UserDetails): UserDetailsDto {
  return {
    ...<UserDetailsDto>omit(user, 'leadsOf', 'isIntranetVerified', 'usersFromOtherOfficesAccess'),
    leadsOf: Object.keys(pickBy(user.leadsOf, Boolean)),
    userEmail: getDefaultEmail(user.userEmail),
    userToReassignLeadsTo: user.userToReassignLeadsTo?.userId,
    userDataToTransfer: user.userDataToTransfer?.userId,
    // tslint:disable-next-line:no-null-keyword
    sendEmailsFrom: getDefaultEmail(user.sendEmailsFrom || '') || null,
  };
}

export function convertUserDetailsFromDto(user: UserDetailsDto, usersFromOtherOfficesAccess: boolean): UserDetails {
  return defaults(
    {
      ...<UserDetails>omit(user, 'leadsOf'),
      leadsOf: fromPairs((user.leadsOf || []).map((leadId) => [leadId, true])),
      userEmail: getEmailLocalName(user.userEmail),
      sendEmailsFrom: getEmailLocalName(user.sendEmailsFrom || ''),
      isIntranetVerified: Boolean(user.intranetGuid),
      usersFromOtherOfficesAccess,
    },
    {
      confirmations: {
        skipChangeStatusCheck: false,
      },
    });
}

export function mergeUserDetailsWithSalesPersons(user: UserDetails, salesPersons: UserListItem[]): UserDetails {
  return { ...user, leadsOf: defaults(user.leadsOf, fromPairs(salesPersons.map((sp) => [sp.userId, false]))) };
}

export const toUserIntranetCredentials = (user: UserDetails): UserIntranetCredentials =>
  <UserIntranetCredentials>{
    ...pick(user, ['intranetUser', 'intranetPassword']),
    hasRequiredError: false,
  };
